/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react'
import { func, bool } from 'prop-types'
import { useTranslation } from 'react-i18next'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import Icon from 'components/uiElements/Icon'
import { FacebookShareButton, PinterestShareButton, TwitterShareButton, EmailShareButton } from 'react-share'
import { click } from 'utils/tracker'
import { compositionType } from 'models/propTypes'
import BouncingBallLoader from 'components/uiElements/BouncingBallLoader'
import { getBaseUrl } from 'models/location/selectors'
import useSharedComposition from './useSharedComposition'

require('./SharePanel.scss')

const SharePanel = ({ close, composition, isSaving }) => {
    const { uuid, thumbUrl, name } = composition || {}
    const { t } = useTranslation()
    const { sharedCompositionData, error, isLoading } = useSharedComposition(uuid, isSaving)
    const [copied, setCopied] = useState(false)
    useEffect(() => {
        const copiedTimer = setTimeout(() => setCopied(false), 1000)
        return () => clearTimeout(copiedTimer)
    }, [copied])

    const showLoading = isLoading || isSaving

    const url = `${getBaseUrl()}/shared/${uuid}`
    const { publicCompositionImageUrl } = sharedCompositionData
    const defaultCompositionTitle = t('design.default composition name')
    const hasTitle = name !== defaultCompositionTitle

    const quote = `${t('shared.quote')}${hasTitle ? ` "${name}"` : ''}`
    const description = t('shared.description')
    const hashtag = t('shared.hashtag')
    const hashtags = t('shared.hashtags').split(' ')
    const showError = !isSaving && error

    const errMessage = (
        <div className="sharePanelMessage">
            <p>{t('shared.errSavingMessage')}</p>
        </div>
    )

    const trackShareClick = (shareType) =>
        new Promise((resolve) => {
            const { name: compositionName, id, sceneId } = composition
            const trackingInfo = {
                DesignName: compositionName,
                DesignId: id,
                RoomId: sceneId,
                ShareType: shareType,
            }
            if (shareType) {
                click('DecorateRoom.ShareDesign', trackingInfo)
            }
            resolve()
        })

    const shareButtons = (
        <div className="shareButtonsContainter">
            <div className="sharePreview">
                <div className="shareImg">
                    {thumbUrl ? (
                        <img src={thumbUrl} alt={t('shared.alt.shared design')} />
                    ) : (
                        <div className="loadingImage" />
                    )}
                </div>
            </div>

            {showLoading ? (
                <div className="loader">
                    <BouncingBallLoader subheaderText={`${t('shared.saving your design')}...`} />
                </div>
            ) : (
                <>
                    <PinterestShareButton
                        media={publicCompositionImageUrl}
                        description={quote}
                        url={url}
                        beforeOnClick={() => trackShareClick('Pinterest')}
                    >
                        <div className="shareButton">
                            <Icon code="pinterest" svg />
                            <div className="shareButtonText">{t('shared.pinterest')}</div>
                        </div>
                    </PinterestShareButton>

                    <FacebookShareButton
                        title={quote}
                        quote={hasTitle ? name : ''}
                        url={url}
                        hashtag={hashtag}
                        beforeOnClick={() => trackShareClick('Facebook')}
                    >
                        <div className="shareButton">
                            <Icon code="facebook" svg />
                            <div className="shareButtonText">{t('shared.facebook')}</div>
                        </div>
                    </FacebookShareButton>

                    <TwitterShareButton
                        title={quote}
                        url={url}
                        hashtags={hashtags}
                        beforeOnClick={() => trackShareClick('Twitter')}
                    >
                        <div className="shareButton">
                            <Icon code="twitter" svg />
                            <div className="shareButtonText">{t('shared.twitter')}</div>
                        </div>
                    </TwitterShareButton>

                    <EmailShareButton
                        subject={quote}
                        url={url}
                        body={description}
                        separator={'\n'}
                        beforeOnClick={() => trackShareClick('Email')}
                    >
                        <div className="shareButton">
                            <Icon code="mail" svg />
                            <div className="shareButtonText">{t('common.email')}</div>
                        </div>
                    </EmailShareButton>
                    <CopyToClipboard
                        text={url}
                        onCopy={() => {
                            setCopied(true)
                            trackShareClick('CopyLink')
                        }}
                    >
                        <button className="shareButton">
                            <Icon code="link" svg />
                            <div className="shareButtonText">{t('shared.copy link')}</div>
                            {copied && <Icon code="checkmark" className="fadeOut" />}
                        </button>
                    </CopyToClipboard>
                </>
            )}
        </div>
    )

    return (
        <div className="SharePanel">
            <div className="sharePanelInner">
                <div className="scrollable">
                    <div className="sharePanelHeader">
                        <h3>{t('shared.share your design')}</h3>
                        <button onClick={close} data-testid="sharepanel-close-btn">
                            <Icon code="cross" />
                        </button>
                    </div>
                    {showError ? errMessage : shareButtons}
                </div>
            </div>
        </div>
    )
}

SharePanel.defaultProps = {
    composition: null,
}

SharePanel.propTypes = {
    close: func.isRequired,
    composition: compositionType,
    isSaving: bool.isRequired,
}

export default SharePanel
